import identityDatashape from "./identity.datashape";
import shareholderIdentityDatashape from "./shareholderIdentity.datashape";

/**
 * Education details are present on a personal profile whose `professionDetails.status`
 * is STUDENT. The recipient-only fields (studentId onwards) are null for a provider.
 */
const educationDetailsDatashape = `
    educationDetails {
      institutionType
      institutionTypeOther
      institutionName
      branch
      studentId
      faculty
      degree
      yearOfStudy
      expectedGraduationYear
      institutionAddress {
        address
        city
        country
        primaryPhone
        secondaryPhone
        fax
        email
        website
      }
    }
`;

/** Shared by the provider and recipient NGO profiles. */
const ngoCommonDatashape = `
    entityType
    accountType
    ngoDetails {
      name
      activity
      recognitionNumber
      recognitionPlace
      recognitionDate
      taxIdentificationNumber
      countryOfActivity
      registrationDocument
    }
    administrativeBoard {
      name { first middle last }
      nationality
      identity {
        ${shareholderIdentityDatashape}
      }
    }
    businessAddress {
      address
      city
      country
      primaryPhone
      secondaryPhone
      email
      website
    }
`;

/**
 * The settlement account every provider gives during onboarding. It exists only on the
 * unmasked provider types — a counterparty never sees it — so this selection is valid here
 * and would be a schema error against a masked profile.
 */
const bankDetailsDatashape = `
    bankDetails {
      accountHolderName
      bankName
      bankBranch
      iban
      swiftCode
    }
`;

const profileDatashape = `
  ... on ProviderPersonalProfileType {
    entityType
    accountType
    ${bankDetailsDatashape}
    identity {
      ${identityDatashape}
    }
    businessAddress {
      address
      city
      country
      primaryPhone
      secondaryPhone
      email
      website
    }
    personalDetails {
      fullName { first middle last }
      motherName { first middle last }
      placeOfBirth
      dateOfBirth
      nationality
      countryOfResidence
      maritalStatus
      spouseName { first middle last }
    }
    professionDetails {
      status
      profession
      employerName
      businessActivity
    }
    ${educationDetailsDatashape}
    residentialAddress {
      address
      city
      country
      primaryPhone
      secondaryPhone
      email
    }
  }

  ... on RecipientPersonalProfileType {
    entityType
    accountType
    identity {
      ${identityDatashape}
    }
    personalDetails {
      fullName { first middle last }
      motherName { first middle last }
      placeOfBirth
      dateOfBirth
      nationality
      countryOfResidence
      maritalStatus
      spouseName { first middle last }
    }
    professionDetails {
      status
      profession
      employerName
      businessActivity
    }
    ${educationDetailsDatashape}
    residentialAddress {
      address
      city
      country
      primaryPhone
      secondaryPhone
      email
    }
    businessAddress {
      address
      city
      country
      primaryPhone
      secondaryPhone
      email
      website
    }
    personalFinancialDetails {
      incomeApplicable
      income
      incomePeriodicity { value otherDetails }
      sourcesOfIncome { source otherDetails }
      totalLiabilities { applicable amount }
      totalFixedAssets { applicable amount }
    }
    personalEvidencingDocuments {
      type
      otherDetails
      document
    }
    relationsWithBanks {
      name
      country
    }
  }

  ... on ProviderCorporationProfileType {
    entityType
    accountType
    ${bankDetailsDatashape}
    corporationDetails {
      name
      businessActivity
      typeOfTradingBusiness
      registrationNumber
      placeOfRegistration
      dateOfRegistration
      countryOfAuthority
      registrationDocument
      legalForm
    }
    shareholders {
      name { first middle last }
      percentage
      nationality
      identity {
        ${shareholderIdentityDatashape}
      }
    }
    businessAddress {
      address
      city
      country
      primaryPhone
      secondaryPhone
      email
      website
    }
  }

  ... on RecipientCorporationProfileType {
    entityType
    accountType
    corporationDetails {
      name
      businessActivity
      typeOfTradingBusiness
      registrationNumber
      placeOfRegistration
      dateOfRegistration
      countryOfAuthority
      registrationDocument
      legalForm
    }
    shareholders {
      name { first middle last }
      percentage
      nationality
      identity {
        ${shareholderIdentityDatashape}
      }
    }
    businessAddress {
      address
      city
      country
      primaryPhone
      secondaryPhone
      email
      website
    }
    corporationEvidencingDocuments {
      type
      otherDetails
      document
    }
    corporationFinancialDetails {
      totalSales { previousYear expected }
      netProfit { previousYear expected }
      exportSales { previousYear expected }
      receivables { applicable amount }
      inventory { applicable amount }
      fixedAssets { applicable amount }
      bankDebt { applicable amount }
      otherDebt { applicable amount }
    }
    majorClients {
      name
      country
    }
    majorSuppliers {
      name
      country
    }
    relationsWithBanks {
      name
      country
    }
  }

  ... on ProviderNgoProfileType {
    ${ngoCommonDatashape}
    ${bankDetailsDatashape}
  }

  ... on RecipientNgoProfileType {
    ${ngoCommonDatashape}
    ngoFinancialDetails {
      totalIncome { previousYear expected }
      totalExpenses { previousYear expected }
      fixedAssets { applicable amount }
      bankDebt { applicable amount }
      otherDebt { applicable amount }
    }
    ngoEvidencingDocuments {
      type
      otherDetails
      document
    }
    majorDonors {
      name
      country
    }
    relationsWithBanks {
      name
      country
    }
  }
`;

export default profileDatashape;
